import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { Component, OnDestroy, Renderer2, RendererFactory2 } from "@angular/core";
import { TranslocoService } from "@jsverse/transloco";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { DocumentFieldEmitterService } from "app/global/documentfield-emitter.service";
import { clearButtonForNg } from "app/global/flatpickerButtonPlugin";
import { GlobalService } from "app/global/global.service";
import { IntegrationService } from "app/global/integrations/integrations.service";
import { DroplistOptions, Field } from "app/interfaces/fields";
import { MomentPipe } from "app/pipes/moment.pipe";
import flatpickr from "flatpickr";
import { DocumentHelperFacade } from "../../documents/document-right-sidebar/document-helper.facade";
import { IFieldVisibilityType } from "../../edit-template/field-visibility.dto";
import { ISearcableMetaData, SearchMetaDataModalComponent, ViewEnum } from "../../general-modals/search-meta-data-modal/search-meta-data-modal.component";
import { FieldTypes } from "../general-popover-settings/general-popover-settings.dto";
import { FieldsBuilderService } from "./fields-builder.service";
import { FieldsDataHelperService } from "./fields-data-helper.service";


@Component({
    template: ''
})

export abstract class AbstractUpdateFieldsComponent implements OnDestroy {

    private updateTimeout?: NodeJS.Timeout | null = null;
    protected renderer: Renderer2;

    constructor(
        public _globalService: GlobalService,
        public documentFieldsEmitter: DocumentFieldEmitterService,
        public _momentPipe$: MomentPipe,
        rendererFactory: RendererFactory2,
        public _integrationService: IntegrationService,
        public _modalService: NgbModal,
        public _fieldsBuilderService: FieldsBuilderService,
        public _translocoService: TranslocoService,
        public _documentHelperFacade: DocumentHelperFacade,
        public _fieldsDataHelperService: FieldsDataHelperService
    ) { this.renderer = rendererFactory.createRenderer(null, null); }





    /**
     * Update the field data for a signature field.
     * @param uuid - The unique identifier for the field.
     * @param signature - The signature field data to update.
     * @returns {void}
     * @example
     * ```typescript
     * this.updateInputFields('1234-5678-9012', { id: 1, name: 'Text', field_info: 'document_field' });
     * ``` 
     */
    updateInputFields(uuid: string | number, textfield: FieldTypes, isRecipientField = {}, fieldSelectedType: string, suppressRefresh: boolean = false): void {
        this._fieldsDataHelperService.updateInputFieldsData(uuid, textfield, isRecipientField, fieldSelectedType, suppressRefresh);
    }




    /**
     * Update the field data for a text field.
     * @param uuid - The unique identifier for the field.
     * @param textfield - The text field data to update.
     * @returns {void}
     * @example
     * ```typescript
     * this.updateDropListData('1234-5678-9012', { id: 1, name: 'Text', field_info: 'document_field' });
     * ``` 
     */
    updateDropListData(uuid: string | number, droplist: FieldTypes, suppressRefresh: boolean = false): void {
        this._fieldsDataHelperService.updateDropListFieldsData(uuid, droplist, suppressRefresh);
    }






   
    /**
     * Extracts field data using the underlying helper service.
     *
     * @param skipFilteration - If true, skips the filtration process when extracting fields data. Defaults to false.
     * @returns An array of `Field` objects representing the extracted fields data.
     */
    extractFieldsData(skipFilteration: boolean = false): Field[] {
        return this._fieldsDataHelperService.extractFieldsData(skipFilteration);
    }









    /**
     * Synchronizes the text field value with the document field.
     * @param $event - The input event containing the new value.
     * @param documentfield - The document field to update.
     * @returns {void}
     * @example
     * ```typescript
     * this.syncDocumentTextfield({ target: { value: 'Hello' } }, { uuid: '1234-5678-9012' });
     * ``` 
     */
    syncDocumentTextfield($event: InputEvent, documentfield: Field): void {
        this._fieldsDataHelperService.syncTextFieldsData($event, documentfield);
    }





    /**
     * Sync document datepicker field to content
     * @param $event which is giving current value of target dom
     * @param documentfield which is giving current object of target dom
     * @returns {void}
     * @example
     * ```typescript
     * this.syncDocumentDatepickerfield({ target: { value: '2021-01-01' } }, { uuid: '1234-5678-9012' });
     * ``` 
     */
    async syncDocumentDatepickerfield($event: InputEvent, documentfield: Field) {
        this._fieldsDataHelperService.syncDatePickerFieldsData($event, documentfield);
        this.clearButton.removeEventListener('click', () => { });
    }



    /**
     * Sync document droplist field to content
     * @param option which is giving current value of target dom
     * @param documentfield which is giving current object of target dom
     * @returns {void}
     * @example
     * ```typescript 
     * this.syncDocumentDroplistField('option1', { uuid: '1234-5678-9012' });
     * ``` 
     */
    syncDocumentDroplistField(optionValue: string, documentfield: Field): void {
        this._fieldsDataHelperService.syncDropListFieldsData(optionValue, documentfield);
    }



    /**
     * Sync multi-select field to content
     * @param $event which is giving current value of target dom
     * @param multiSelectField which is giving current object of target dom
     * @returns {void}
     * @example
     * ```typescript
     * this.syncMultiSelectField([{ id: '1', name: 'Option 1' }, { id: '2', name: 'Option 2' }], { uuid: '1234-5678-9012' });
     * ``` 
     * @return {void}
     */
    syncMultiSelectField($event: DroplistOptions[], multiSelectField: Field) {
        this._fieldsDataHelperService.syncMultiSelectFieldsData($event, multiSelectField);
    }




    /**
     * Sync document list field to content
     * @param $event which is giving current value of target dom
     * @param multichoiceField which is giving current object of target dom
     * @returns {void}
     * @example
     * ```typescript
     * this.syncDocumentListField({ name: 'list', value: 'option1' }, { uuid: '1234-5678-9012' });
     * ``` 
     */
    syncDocumentListField($event: DroplistOptions | DroplistOptions[], multichoiceField: Field): void {
        this._fieldsDataHelperService.syncDocumentListFieldsData($event, multichoiceField);
    }




    /**
     * Sync document checkbox field to content
     * @param $event which is giving current value of target dom
     * @param documentfield which is giving current object of target dom
     * @returns {void}
     * @example
     * ```typescript
     * this.syncDocumentCheckboxField({ name: 'checkbox', value: 1 }, { uuid: '1234-5678-9012' });
     * ``` 
     */
    syncDocumentCheckboxField($event: { name: string, value: 0 | 1 }, documentfield: Field): void {
        this._fieldsDataHelperService.syncDocumentCheckBoxFieldsData($event, documentfield);
    }



    /**
     * Sync document radio field to content
     * @param $event which is giving current value of target dom
     * @param documentfield which is giving current object of target dom
     */
    onReadyDatepicker({ instance }: { instance: flatpickr.Instance }): void {
        clearButtonForNg(this.clearButton, instance);
    }





    /**
     * Open the search meta data modal
     * @returns {void}
     * @example
     * ```typescript
     * this.openMetaDataModal(0, 1); // isSecretField, isSemiHiddenField
     * ```
     */
    openMetaDataModal(fieldVisibilityType: IFieldVisibilityType): void {
        const searchMetaDataModal: NgbModalRef = this._modalService.open(SearchMetaDataModalComponent, {
            windowClass: 'search-meta-data-modal',
            backdrop: 'static'
        });
        searchMetaDataModal.componentInstance.view = ViewEnum.DOCUMENT_FIELD;
        searchMetaDataModal.result.then(async (result: ISearcableMetaData) => {
            if (result) {
                this.proceedFieldsDataToUpdate(result, fieldVisibilityType);
            }
        });
    }




    /**
     * Processes and updates field data based on the provided metadata result.
     * 
     * This method performs several checks to prevent duplicate or conflicting field entries:
     * - Checks if the field already exists as a secret hidden field and shows a warning if so.
     * - Checks if the field exists as a semi hidden field and shows a warning if so.
     * - Checks if the field exists in the document content and shows a warning if so.
     * - If the field is a duplicate, prompts the user for confirmation before replacing its configuration.
     * 
     * Depending on the `meta_data_type` of the result, it extracts the appropriate field object
     * and adds it to the global fields data. It then updates related fields, triggers integration
     * logic if necessary, and refreshes the fields data.
     * 
     * @param result - The metadata object containing information about the field to be processed.
     * @returns A promise that resolves when the field data has been processed and updated.
     */
    async proceedFieldsDataToUpdate(result: ISearcableMetaData, fieldVisibilityType: IFieldVisibilityType): Promise<void> {
        this._fieldsDataHelperService.proceedFieldsDataToUpdate(result, fieldVisibilityType);
        this.reFreshFieldsData(fieldVisibilityType.is_secret_hidden_field ? 1 : 0);
    }




    /**
     * Refresh the fields data based on the secret_hidden_field value
     * @param secret_hidden_field 
     * @returns {void}
     * @example
     * ```typescript
     * this.reFreshFieldsData(1); // getFieldsData
     * this.reFreshFieldsData(0); // updateFieldsData
     * ```
     */
    private reFreshFieldsData(secret_hidden_field: number): void {
        secret_hidden_field === 1 ?
            this.getFieldsData() :
            this.updateFieldsData();
    }





    
    /**
     * Handles the drag-and-drop event for reordering fields.
     * 
     * This method updates the order of items in the `fields_data` array based on the drag event,
     * and then iterates through the array to update each field's order in the global service.
     *
     * @param event - The drag-and-drop event containing the previous and current indices of the moved item.
     */
    onDragEnd(event: CdkDragDrop<[]>): void {
        moveItemInArray(this.fields_data, event.previousIndex, event.currentIndex);
        for (let index = 0; index < this.fields_data.length; index++) {
            const field = this.fields_data[index];
            this._globalService.updateField(field['uuid'], { order: index + 1 });
        }
    }




    
    /**
     * Updates the `fields_data` property by extracting field data and filtering out fields
     * that are either hidden or marked as secret hidden fields.
     *
     * This method first clears the existing `fields_data` array, then populates it with
     * the result of `extractFieldsData()`, excluding any fields where the `hidden` or
     * `secret_hidden_field` properties are set to `true`.
     *
     * @remarks
     * - Relies on the `extractFieldsData` method to retrieve the initial list of fields.
     * - Only fields that are visible and not secret are retained in `fields_data`.
     */
    updateFieldsData(): void {
        this.fields_data = this.extractFieldsData().filter((field: Field) => !field.hidden && !field.secret_hidden_field);
    }


    public fields_data: Field[] = [];
    protected abstract clearButton: HTMLElement;
    protected getFieldsData?(): void { }


    ngOnDestroy(): void {
        if (this.updateTimeout) {
            clearTimeout(this.updateTimeout);
            this.updateTimeout = null;
        }
    }
}